import { useState, useEffect } from "react";
import { StoreLayout } from "../../components/StoreLayout";
import { api, apiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Checkbox } from "../../components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
  UploadSimple, FileText, CheckCircle, ShieldCheck, CurrencyInr, Headset, Briefcase,
  MapPin, Plus, X, ArrowRight, ArrowLeft, EnvelopeSimple, Bank, Check,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { PARTNER_BENEFITS, PARTNER_TERMS } from "../../lib/storefront";
import { openRazorpayCheckout } from "../../lib/razorpay";

const ICONS = { Briefcase, CurrencyInr, ShieldCheck, Headset };
const CITY_SUGGESTIONS = ["Mumbai", "Delhi", "Bengaluru", "Hyderabad", "Pune", "Chennai", "Kolkata", "Ahmedabad", "Jaipur", "Surat", "Lucknow", "Kanpur", "Nagpur", "Indore", "Bhopal", "Patna", "Chandigarh", "Kochi", "Coimbatore", "Visakhapatnam", "Vadodara", "Noida", "Gurugram", "Thane", "Nashik"];
const STEPS = ["Business", "Services", "Documents", "Bank", "Review"];
const DOC_TYPES = [
  { key: "aadhaar", label: "Aadhaar Card", required: true },
  { key: "pan", label: "PAN Card", required: true },
  { key: "gst", label: "GST Certificate", required: false },
  { key: "msme", label: "MSME / Udyog Aadhaar", required: false },
];

const EMPTY = {
  name: "", contact_person: "", email: "", phone: "", address: "",
  pincode: "", city: "", state: "", gstin: "", msme: "", experience: "", notes: "",
};

const SELL_PARTNER_TERMS = `FIXIGO SELL DEVICE PICKUP — PARTNER TERMS

1. DEPOSIT: A refundable security deposit is required before receiving device pickups. Fixigo's platform fee for each completed pickup is auto-deducted from this deposit.

2. ALLOCATION: Pickups are auto-assigned based on your covered pincodes and current load. Allocation pauses automatically if your deposit balance falls below the agreed threshold.

3. PICKUP CONDUCT: Verify device condition against the customer's declared answers. Pay the customer the confirmed/final offer amount at pickup. Handle devices with care and maintain data-wipe confidentiality.

4. QUALITY & DISPUTES: Any discrepancy in device condition must be reported before completing the pickup. Fixigo's QC/final price determination is binding for settlement.

5. SETTLEMENT: Device payouts and platform-fee deductions are reconciled via your deposit wallet ledger. Low-balance top-ups must be maintained to continue operations.

6. COMPLIANCE: You agree to follow all applicable e-waste, resale and data-protection regulations.`;

export default function BecomePartner() {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(EMPTY);
  const [appId, setAppId] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [banks, setBanks] = useState([]);
  const [services, setServices] = useState([]); // [{category, service}]
  const [areas, setAreas] = useState([]); // [{city, pincodes:[{pincode,area}]}]
  const [verticals, setVerticals] = useState(["service"]);
  const [sellAreas, setSellAreas] = useState([]);
  const [sellCityInput, setSellCityInput] = useState("");
  const [acceptSell, setAcceptSell] = useState(false);
  const [bank, setBank] = useState({ account_number: "", ifsc: "", bank_name: "", account_holder: "" });
  const [bankOther, setBankOther] = useState(false);
  const [docs, setDocs] = useState([]); // [{id, doc_type, original_filename}]
  const [accept, setAccept] = useState(false);
  const [pinLoading, setPinLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [otpStage, setOtpStage] = useState(false);
  const [otp, setOtp] = useState("");
  const [cityInput, setCityInput] = useState("");
  const [done, setDone] = useState(false);
  const [fee, setFee] = useState(null); // {fee, tier}
  const [payAfterVerify, setPayAfterVerify] = useState(false);
  const [paid, setPaid] = useState(false);

  useEffect(() => {
    api.get("/public/service-catalog").then((r) => setCatalog(r.data)).catch(() => {});
    api.get("/public/banks").then((r) => setBanks(r.data)).catch(() => {});
  }, []);

  // Recompute onboarding fee whenever the review step is reached or services change
  useEffect(() => {
    if (step !== 4) return;
    api.post("/public/partner-onboarding-fee", { services_offered: services })
      .then((r) => setFee(r.data)).catch(() => {});
  }, [step, services]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const payload = () => ({
    ...form, email: form.email, services_offered: services, service_areas: areas,
    verticals, sell_areas: sellAreas, accept_sell_terms: acceptSell,
    bank, accept_terms: accept,
  });

  const hasSell = verticals.includes("sell");
  const hasServiceVertical = verticals.includes("service");
  const toggleVertical = (v) => setVerticals((prev) => prev.includes(v) ? (prev.length > 1 ? prev.filter((x) => x !== v) : prev) : [...prev, v]);

  // sell-area helpers (mirror service areas)
  const addSellCity = (city) => { if (!city || sellAreas.find((a) => a.city === city)) return; setSellAreas((a) => [...a, { city, pincodes: [] }]); };
  const removeSellCity = (city) => setSellAreas((a) => a.filter((x) => x.city !== city));
  const addSellPincode = async (city, pin) => {
    if (!/^\d{6}$/.test(pin)) { toast.error("Enter a valid 6-digit pincode"); return; }
    let area = "";
    try { const { data } = await api.get(`/public/pincode/${pin}`); area = data.areas?.[0] || data.city || ""; } catch (e) {}
    setSellAreas((prev) => prev.map((a) => a.city === city
      ? { ...a, pincodes: a.pincodes.find((p) => p.pincode === pin) ? a.pincodes : [...a.pincodes, { pincode: pin, area }] } : a));
  };
  const removeSellPincode = (city, pin) => setSellAreas((prev) => prev.map((a) => a.city === city
    ? { ...a, pincodes: a.pincodes.filter((p) => p.pincode !== pin) } : a));

  const lookupPin = async (pin) => {
    set("pincode", pin);
    if (!/^\d{6}$/.test(pin)) return;
    setPinLoading(true);
    try {
      const { data } = await api.get(`/public/pincode/${pin}`);
      setForm((f) => ({ ...f, pincode: pin, city: data.city, state: data.state }));
    } catch (e) { /* user can fill manually */ }
    finally { setPinLoading(false); }
  };

  // create or update draft, returns id
  const persist = async () => {
    if (!appId) {
      const { data } = await api.post("/partner-applications", payload());
      setAppId(data.id);
      return data.id;
    }
    await api.put(`/partner-applications/${appId}`, payload());
    return appId;
  };

  const next = async () => {
    if (step === 0) {
      if (!form.name || !form.email || !form.phone) { toast.error("Name, email and phone are required"); return; }
      if (!form.address || !form.pincode) { toast.error("Address and pincode are required"); return; }
    }
    setBusy(true);
    try {
      await persist();
      if (step === 0) toast.success("Progress saved");
      setStep((s) => Math.min(s + 1, STEPS.length - 1));
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const toggleService = (category, service) => {
    setServices((prev) => {
      const exists = prev.find((x) => x.category === category && x.service === service);
      return exists ? prev.filter((x) => !(x.category === category && x.service === service))
                    : [...prev, { category, service }];
    });
  };
  const hasService = (c, s) => services.some((x) => x.category === c && x.service === s);

  const addCity = (city) => {
    if (!city || areas.find((a) => a.city === city)) return;
    setAreas((a) => [...a, { city, pincodes: [] }]);
  };
  const removeCity = (city) => setAreas((a) => a.filter((x) => x.city !== city));
  const addPincode = async (city, pin) => {
    if (!/^\d{6}$/.test(pin)) { toast.error("Enter a valid 6-digit pincode"); return; }
    let area = "";
    try { const { data } = await api.get(`/public/pincode/${pin}`); area = data.areas?.[0] || data.city || ""; } catch (e) {}
    setAreas((prev) => prev.map((a) => a.city === city
      ? { ...a, pincodes: a.pincodes.find((p) => p.pincode === pin) ? a.pincodes : [...a.pincodes, { pincode: pin, area }] }
      : a));
  };
  const removePincode = (city, pin) => setAreas((prev) => prev.map((a) => a.city === city
    ? { ...a, pincodes: a.pincodes.filter((p) => p.pincode !== pin) } : a));

  const uploadDoc = async (docType, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const id = await persist();
    const fd = new FormData();
    fd.append("doc_type", docType);
    fd.append("file", file);
    try {
      const { data } = await api.post(`/partner-applications/${id}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDocs((d) => [...d.filter((x) => x.doc_type !== docType), data]);
      toast.success("Uploaded");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const docFor = (t) => docs.find((d) => d.doc_type === t);

  const submitForOtp = async () => {
    if (!accept) { toast.error("Please accept the Partner Terms & Conditions"); return; }
    if (hasSell && !acceptSell) { toast.error("Please accept the Sell Device Pickup Terms"); return; }
    if (!docFor("aadhaar") || !docFor("pan")) { toast.error("Aadhaar and PAN are required"); setStep(2); return; }
    if (!bank.account_number || !bank.ifsc || !bank.bank_name) { toast.error("Please complete bank details"); setStep(3); return; }
    setBusy(true);
    try {
      const id = await persist();
      await api.post(`/partner-applications/${id}/send-otp`);
      setOtpStage(true);
      toast.success(`Verification code sent to ${form.email}`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const submitFor = (withPay) => { setPayAfterVerify(withPay); submitForOtp(); };

  const payOnboarding = async () => {
    try {
      const { data } = await api.post(`/partner-applications/${appId}/onboarding/create-order`);
      const res = await openRazorpayCheckout(
        { razorpay_order_id: data.razorpay_order_id, amount: data.amount, key_id: data.key_id, prefill: data.prefill },
        { description: "Partner Onboarding Fee" });
      await api.post(`/partner-applications/${appId}/onboarding/verify`, {
        razorpay_order_id: data.razorpay_order_id, razorpay_payment_id: res.razorpay_payment_id, razorpay_signature: res.razorpay_signature });
      setPaid(true);
    } catch (e) {
      toast.message("Application submitted. You can pay the onboarding fee later via the link we'll email you.");
    }
  };

  const verifyOtp = async () => {
    if (otp.length !== 6) { toast.error("Enter the 6-digit code"); return; }
    setBusy(true);
    try {
      await api.post(`/partner-applications/${appId}/verify-otp`, { otp });
      if (payAfterVerify) await payOnboarding();
      setDone(true);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (done) {
    return (
      <StoreLayout>
        <div className="max-w-xl mx-auto px-5 py-24 text-center" data-testid="application-done">
          <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto">
            <CheckCircle size={44} weight="fill" className="text-green-600" />
          </div>
          <h1 className="font-[Outfit] text-3xl font-extrabold tracking-tight mt-6">Application Submitted!</h1>
          {paid ? (
            <p className="text-[#475569] mt-3">Your email is verified and your onboarding fee is <strong className="text-green-600">paid</strong>. Our team will review your details &amp; documents and email your login credentials once approved.</p>
          ) : (
            <p className="text-[#475569] mt-3">Your email is verified and your application is in. Our team will review your details &amp; documents, share an onboarding-fee payment link, and email your login credentials once approved.</p>
          )}
          <Button className="rounded-xl mt-8 bg-[#4F46E5] hover:bg-[#4338CA] text-white font-bold" onClick={() => (window.location.href = "/")}>Back to Home</Button>
        </div>
      </StoreLayout>
    );
  }

  return (
    <StoreLayout>
      {/* Hero + benefits */}
      <section className="bg-[#1E293B] text-white">
        <div className="max-w-7xl mx-auto px-5 py-12">
          <p className="uppercase text-xs tracking-[0.2em] font-semibold text-[#C7D2FE]">Partner with us</p>
          <h1 className="font-[Outfit] text-4xl sm:text-5xl font-extrabold tracking-tight mt-2">Become a Fixigo Partner</h1>
          <p className="text-white/70 mt-3 max-w-xl">Grow your home-services business with steady, verified jobs and reliable payouts.</p>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
            {PARTNER_BENEFITS.map((b) => {
              const Ic = ICONS[b.icon] || ShieldCheck;
              return (
                <div key={b.title} className="bg-white/5 border border-white/10 rounded-2xl p-4">
                  <Ic size={22} weight="bold" className="text-[#C7D2FE]" />
                  <p className="font-[Outfit] font-bold mt-2.5">{b.title}</p>
                  <p className="text-xs text-white/60 mt-1 leading-relaxed">{b.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <div className="max-w-2xl mx-auto px-5 py-10">
        {/* Progress indicator */}
        <div className="flex items-center justify-between mb-8" data-testid="wizard-progress">
          {STEPS.map((label, i) => (
            <div key={label} className="flex items-center flex-1 last:flex-none">
              <div className="flex flex-col items-center">
                <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold border-2 transition-colors duration-200 ${
                  i < step ? "bg-[#4F46E5] border-[#4F46E5] text-white" : i === step ? "border-[#4F46E5] text-[#4F46E5] bg-white" : "border-[#E2E8F0] text-[#94a3b8] bg-white"}`}>
                  {i < step ? <Check size={16} weight="bold" /> : i + 1}
                </div>
                <span className={`text-[10px] mt-1.5 font-semibold ${i <= step ? "text-[#1E293B]" : "text-[#94a3b8]"}`}>{label}</span>
              </div>
              {i < STEPS.length - 1 && <div className={`h-0.5 flex-1 mx-1 mb-4 ${i < step ? "bg-[#4F46E5]" : "bg-[#E2E8F0]"}`} />}
            </div>
          ))}
        </div>

        <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6 md:p-8" data-testid="partner-application-form">
          {/* STEP 1: Business */}
          {step === 0 && (
            <div className="space-y-4">
              <div>
                <h2 className="font-[Outfit] text-xl font-bold">What do you want to partner for?</h2>
                <p className="text-sm text-[#475569] mt-1">Choose one or both. You can operate each vertical independently.</p>
                <div className="grid grid-cols-2 gap-3 mt-3" data-testid="vertical-select">
                  {[{ k: "service", t: "Service Jobs", d: "Repairs, installation, cleaning etc." },
                    { k: "sell", t: "Sell Device Pickups", d: "Collect devices customers sell to Fixigo." }].map((v) => (
                    <button key={v.k} type="button" onClick={() => toggleVertical(v.k)} data-testid={`vertical-${v.k}`}
                      className={`text-left rounded-xl border-2 p-3.5 transition-colors ${verticals.includes(v.k) ? "border-[#4F46E5] bg-[#eef2ff]" : "border-[#E2E8F0] hover:border-[#4F46E5]"}`}>
                      <div className="flex items-center gap-1.5 font-[Outfit] font-bold">{verticals.includes(v.k) && <Check size={14} weight="bold" className="text-[#4F46E5]" />}{v.t}</div>
                      <p className="text-xs text-[#475569] mt-1">{v.d}</p>
                    </button>
                  ))}
                </div>
              </div>
              <h2 className="font-[Outfit] text-xl font-bold pt-2">Business details</h2>
              <Field label="Name / Business Name *"><Input data-testid="app-name" value={form.name} onChange={(e) => set("name", e.target.value)} className="rounded-lg mt-1.5" /></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Contact Person"><Input data-testid="app-contact_person" value={form.contact_person} onChange={(e) => set("contact_person", e.target.value)} className="rounded-lg mt-1.5" /></Field>
                <Field label="Contact No. *"><Input data-testid="app-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="rounded-lg mt-1.5" /></Field>
              </div>
              <Field label="Email *"><Input data-testid="app-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="rounded-lg mt-1.5" /></Field>
              <Field label="Address *"><Textarea data-testid="app-address" value={form.address} onChange={(e) => set("address", e.target.value)} className="rounded-lg mt-1.5" /></Field>
              <div className="grid grid-cols-3 gap-3">
                <Field label="Pincode *">
                  <Input data-testid="app-pincode" value={form.pincode} maxLength={6} onChange={(e) => lookupPin(e.target.value.replace(/\D/g, ""))} className="rounded-lg mt-1.5" />
                  {pinLoading && <span className="text-[11px] text-[#4F46E5]">Looking up…</span>}
                </Field>
                <Field label="City"><Input data-testid="app-city" value={form.city} onChange={(e) => set("city", e.target.value)} className="rounded-lg mt-1.5" /></Field>
                <Field label="State"><Input data-testid="app-state" value={form.state} onChange={(e) => set("state", e.target.value)} className="rounded-lg mt-1.5" /></Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="GSTIN (optional)"><Input data-testid="app-gstin" value={form.gstin} onChange={(e) => set("gstin", e.target.value.toUpperCase())} className="rounded-lg mt-1.5" /></Field>
                <Field label="Experience (optional)"><Input data-testid="app-experience" value={form.experience} onChange={(e) => set("experience", e.target.value)} placeholder="e.g. 5 years" className="rounded-lg mt-1.5" /></Field>
              </div>
            </div>
          )}

          {/* STEP 2: Services & coverage */}
          {step === 1 && (
            <div className="space-y-6">
              {hasServiceVertical && (
              <div>
                <h2 className="font-[Outfit] text-xl font-bold">Services you offer</h2>
                <p className="text-sm text-[#475569] mt-1">Select all services you can provide.</p>
                <div className="space-y-4 mt-4" data-testid="services-offered">
                  {catalog.map((c) => (
                    <div key={c.category}>
                      <p className="text-xs font-bold uppercase tracking-wide text-[#475569] mb-2">{c.category}</p>
                      <div className="flex flex-wrap gap-2">
                        {c.services.map((s) => (
                          <button key={s} type="button" onClick={() => toggleService(c.category, s)}
                            data-testid={`svc-${c.category.replace(/ /g,"-")}-${s.replace(/ /g,"-")}`}
                            className={`px-3 py-1.5 rounded-full text-sm font-semibold border transition-colors duration-200 ${hasService(c.category, s) ? "bg-[#4F46E5] text-white border-[#4F46E5]" : "border-[#E2E8F0] hover:border-[#4F46E5]"}`}>
                            {hasService(c.category, s) && <Check size={13} weight="bold" className="inline mr-1" />}{s}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                  {catalog.length === 0 && <p className="text-sm text-[#475569]">Service list loading…</p>}
                </div>
              </div>
              )}

              {hasServiceVertical && (
              <div className="border-t border-[#E2E8F0] pt-5">
                <h2 className="font-[Outfit] text-xl font-bold">Service areas</h2>
                <p className="text-sm text-[#475569] mt-1">Add any city you operate in, then add the pincodes you cover.</p>
                <div className="flex gap-2 mt-3">
                  <Input list="city-suggestions" value={cityInput} onChange={(e) => setCityInput(e.target.value)}
                    placeholder="Type a city (e.g. Jaipur)" className="rounded-lg" data-testid="add-city-input"
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCity(cityInput.trim()); setCityInput(""); } }} />
                  <datalist id="city-suggestions">{CITY_SUGGESTIONS.map((c) => <option key={c} value={c} />)}</datalist>
                  <Button type="button" variant="outline" className="rounded-lg gap-1 shrink-0" data-testid="add-city-btn"
                    onClick={() => { addCity(cityInput.trim()); setCityInput(""); }}><Plus size={15} /> Add City</Button>
                </div>
                <div className="space-y-3 mt-4" data-testid="service-areas">
                  {areas.map((a) => <CityCoverage key={a.city} area={a} onAddPin={addPincode} onRemovePin={removePincode} onRemoveCity={removeCity} />)}
                </div>
              </div>
              )}

              {hasSell && (
              <div className="border-t border-[#E2E8F0] pt-5">
                <h2 className="font-[Outfit] text-xl font-bold">Sell Device pickup areas</h2>
                <p className="text-sm text-[#475569] mt-1">Cities & pincodes where you'll collect devices. Managed separately from service areas.</p>
                <div className="flex gap-2 mt-3">
                  <Input list="city-suggestions" value={sellCityInput} onChange={(e) => setSellCityInput(e.target.value)}
                    placeholder="Type a city" className="rounded-lg" data-testid="add-sell-city-input"
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addSellCity(sellCityInput.trim()); setSellCityInput(""); } }} />
                  <Button type="button" variant="outline" className="rounded-lg gap-1 shrink-0" data-testid="add-sell-city-btn"
                    onClick={() => { addSellCity(sellCityInput.trim()); setSellCityInput(""); }}><Plus size={15} /> Add City</Button>
                </div>
                <div className="space-y-3 mt-4" data-testid="sell-areas">
                  {sellAreas.map((a) => <CityCoverage key={a.city} area={a} onAddPin={addSellPincode} onRemovePin={removeSellPincode} onRemoveCity={removeSellCity} />)}
                </div>
              </div>
              )}
            </div>
          )}

          {/* STEP 3: Documents */}
          {step === 2 && (
            <div className="space-y-4">
              <h2 className="font-[Outfit] text-xl font-bold">Documents</h2>
              <p className="text-sm text-[#475569]">Upload clear images or PDFs. Aadhaar &amp; PAN are required.</p>
              {DOC_TYPES.map((d) => {
                const uploaded = docFor(d.key);
                return (
                  <div key={d.key} className="flex items-center justify-between border border-[#E2E8F0] rounded-xl p-3.5">
                    <div className="flex items-center gap-3 min-w-0">
                      <FileText size={20} className="text-[#4F46E5] shrink-0" />
                      <div className="min-w-0">
                        <p className="font-semibold text-sm">{d.label} {d.required && <span className="text-rose-500">*</span>}</p>
                        {uploaded && <p className="text-xs text-green-600 truncate flex items-center gap-1"><CheckCircle size={12} weight="fill" /> {uploaded.original_filename}</p>}
                      </div>
                    </div>
                    <label className="shrink-0">
                      <span className="text-sm font-semibold text-[#4F46E5] cursor-pointer hover:underline" data-testid={`upload-${d.key}-label`}>{uploaded ? "Replace" : "Upload"}</span>
                      <input type="file" className="hidden" accept="image/*,application/pdf" data-testid={`upload-${d.key}`} onChange={(e) => uploadDoc(d.key, e)} />
                    </label>
                  </div>
                );
              })}
            </div>
          )}

          {/* STEP 4: Bank */}
          {step === 3 && (
            <div className="space-y-4">
              <h2 className="font-[Outfit] text-xl font-bold flex items-center gap-2"><Bank size={20} weight="bold" className="text-[#4F46E5]" /> Bank details</h2>
              <Field label="Account Holder Name"><Input data-testid="bank-holder" value={bank.account_holder} onChange={(e) => setBank({ ...bank, account_holder: e.target.value })} className="rounded-lg mt-1.5" /></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Account Number *"><Input data-testid="bank-account" value={bank.account_number} onChange={(e) => setBank({ ...bank, account_number: e.target.value.replace(/\D/g, "") })} className="rounded-lg mt-1.5" /></Field>
                <Field label="IFSC Code *"><Input data-testid="bank-ifsc" value={bank.ifsc} onChange={(e) => setBank({ ...bank, ifsc: e.target.value.toUpperCase() })} className="rounded-lg mt-1.5" /></Field>
              </div>
              <Field label="Bank Name *">
                <Select value={bankOther ? "Other" : bank.bank_name} onValueChange={(v) => { if (v === "Other") { setBankOther(true); setBank({ ...bank, bank_name: "" }); } else { setBankOther(false); setBank({ ...bank, bank_name: v }); } }}>
                  <SelectTrigger className="rounded-lg mt-1.5" data-testid="bank-name-select"><SelectValue placeholder="Select bank" /></SelectTrigger>
                  <SelectContent>{banks.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                </Select>
                {bankOther && <Input data-testid="bank-name-other" value={bank.bank_name} onChange={(e) => setBank({ ...bank, bank_name: e.target.value })} placeholder="Enter bank name" className="rounded-lg mt-2" />}
              </Field>
              <div className="flex items-center justify-between border border-[#E2E8F0] rounded-xl p-3.5">
                <div className="flex items-center gap-3"><FileText size={20} className="text-[#4F46E5]" /><div><p className="font-semibold text-sm">Cancelled Cheque / Passbook</p><p className="text-xs text-[#475569]">Optional</p>{docFor("cheque") && <p className="text-xs text-green-600">{docFor("cheque").original_filename}</p>}</div></div>
                <label><span className="text-sm font-semibold text-[#4F46E5] cursor-pointer hover:underline">Upload</span><input type="file" className="hidden" accept="image/*,application/pdf" data-testid="upload-cheque" onChange={(e) => uploadDoc("cheque", e)} /></label>
              </div>
            </div>
          )}

          {/* STEP 5: Review + T&C + OTP */}
          {step === 4 && !otpStage && (
            <div className="space-y-5">
              <h2 className="font-[Outfit] text-xl font-bold">Review &amp; confirm</h2>
              <div className="bg-[#F8FAFC] rounded-xl p-4 text-sm space-y-1.5">
                <Row k="Business" v={form.name} />
                <Row k="Email" v={form.email} />
                <Row k="Phone" v={form.phone} />
                <Row k="Location" v={`${form.city || "—"}, ${form.state || ""} ${form.pincode}`} />
                <Row k="Services" v={services.length ? services.map((s) => s.service).join(", ") : "—"} />
                <Row k="Coverage" v={areas.length ? areas.map((a) => `${a.city} (${a.pincodes.length})`).join(", ") : "—"} />
                <Row k="Verticals" v={verticals.map((v) => v === "sell" ? "Sell Pickups" : "Service").join(", ")} />
                {hasSell && <Row k="Sell coverage" v={sellAreas.length ? sellAreas.map((a) => `${a.city} (${a.pincodes.length})`).join(", ") : "—"} />}
                <Row k="Documents" v={docs.map((d) => d.doc_type).join(", ") || "—"} />
                <Row k="Bank" v={bank.bank_name ? `${bank.bank_name} ••${bank.account_number.slice(-4)}` : "—"} />
              </div>

              <div>
                <Label className="label-caps">Partner Terms &amp; Conditions</Label>
                <div className="mt-2 h-44 overflow-y-auto border border-[#E2E8F0] rounded-xl p-3 text-xs text-[#475569] whitespace-pre-line leading-relaxed" data-testid="partner-terms">{PARTNER_TERMS}</div>
                <label className="flex items-start gap-2.5 mt-3 cursor-pointer">
                  <Checkbox checked={accept} onCheckedChange={setAccept} data-testid="accept-terms" className="mt-0.5" />
                  <span className="text-sm text-[#1E293B]">I have read and accept the Partner Terms &amp; Conditions, including the <strong>90-day service warranty</strong>.</span>
                </label>
              </div>

              {hasSell && (
                <div data-testid="sell-terms-block">
                  <Label className="label-caps">Sell Device Pickup Terms &amp; Conditions</Label>
                  <div className="mt-2 h-40 overflow-y-auto border border-[#E2E8F0] rounded-xl p-3 text-xs text-[#475569] whitespace-pre-line leading-relaxed" data-testid="sell-partner-terms">{SELL_PARTNER_TERMS}</div>
                  <label className="flex items-start gap-2.5 mt-3 cursor-pointer">
                    <Checkbox checked={acceptSell} onCheckedChange={setAcceptSell} data-testid="accept-sell-terms" className="mt-0.5" />
                    <span className="text-sm text-[#1E293B]">I accept the Sell Device Pickup Partner Terms, including the <strong>refundable deposit &amp; per-pickup platform fee</strong>.</span>
                  </label>
                </div>
              )}

              {fee && (
                <div className="border border-[#E2E8F0] rounded-xl p-4 bg-[#F8FAFC]" data-testid="onboarding-fee-block">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <CurrencyInr size={20} weight="bold" className="text-[#4F46E5]" />
                      <div>
                        <p className="font-[Outfit] font-bold text-[#1E293B]">One-time Onboarding Fee</p>
                        <p className="text-xs text-[#475569]">{fee.tier === "cleaning" ? "Home Cleaning partner" : "Appliance repair partner (AC / Refrigerator / Washing Machine / TV)"}</p>
                      </div>
                    </div>
                    <span className="font-[Outfit] text-2xl font-extrabold text-[#4F46E5]" data-testid="onboarding-fee-amount">₹{fee.fee}</span>
                  </div>
                  <p className="text-xs text-[#475569] mt-2">Pay now to fast-track activation, or submit now and pay later via a secure link our team will send you.</p>
                </div>
              )}

              <div className="space-y-2.5">
                <Button className="rounded-xl w-full h-11 bg-[#4F46E5] hover:bg-[#4338CA] text-white font-bold gap-2 disabled:opacity-60" onClick={() => submitFor(true)} disabled={busy || !accept || (hasSell && !acceptSell)} data-testid="pay-and-submit">
                  <CurrencyInr size={18} weight="bold" /> {busy ? "Please wait…" : `Pay ₹${fee?.fee ?? ""} Now & Submit`}
                </Button>
                <Button variant="outline" className="rounded-xl w-full h-11 border-[#4F46E5] text-[#4F46E5] hover:bg-[#eef2ff] font-bold gap-2 disabled:opacity-60" onClick={() => submitFor(false)} disabled={busy || !accept || (hasSell && !acceptSell)} data-testid="submit-application">
                  <EnvelopeSimple size={18} weight="bold" /> {busy ? "Please wait…" : "Submit without paying"}
                </Button>
              </div>
            </div>
          )}

          {/* OTP entry */}
          {step === 4 && otpStage && (
            <div className="space-y-5 text-center" data-testid="otp-stage">
              <div className="w-16 h-16 rounded-full bg-[#eef2ff] flex items-center justify-center mx-auto"><EnvelopeSimple size={30} weight="bold" className="text-[#4F46E5]" /></div>
              <h2 className="font-[Outfit] text-xl font-bold">Verify your email</h2>
              <p className="text-sm text-[#475569]">We sent a 6-digit code to <strong>{form.email}</strong>. Enter it below to submit your application.</p>
              <Input value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))} maxLength={6} placeholder="000000"
                className="rounded-xl text-center text-2xl tracking-[0.4em] font-bold h-14 max-w-[220px] mx-auto" data-testid="otp-input" />
              <Button className="rounded-xl w-full h-11 bg-[#4F46E5] hover:bg-[#4338CA] text-white font-bold" onClick={verifyOtp} disabled={busy} data-testid="verify-otp">{busy ? "Verifying…" : (payAfterVerify ? "Verify & Pay Now" : "Submit Application")}</Button>
              <button className="text-sm text-[#4F46E5] font-semibold hover:underline" onClick={submitForOtp} data-testid="resend-otp">Resend code</button>
            </div>
          )}

          {/* Nav buttons */}
          {!(step === 4 && otpStage) && (
            <div className="flex items-center justify-between mt-7 pt-5 border-t border-[#E2E8F0]">
              <Button variant="ghost" className="rounded-xl gap-1.5" onClick={back} disabled={step === 0} data-testid="wizard-back"><ArrowLeft size={16} /> Back</Button>
              {step < STEPS.length - 1 && (
                <Button className="rounded-xl gap-1.5 bg-[#4F46E5] hover:bg-[#4338CA] text-white font-bold" onClick={next} disabled={busy} data-testid="wizard-next">
                  {busy ? "Saving…" : "Next"} <ArrowRight size={16} weight="bold" />
                </Button>
              )}
            </div>
          )}
        </div>
        <p className="text-xs text-[#94a3b8] text-center mt-4">Your progress is saved automatically as you go.</p>
      </div>
    </StoreLayout>
  );
}

const Field = ({ label, children }) => (
  <div><Label className="label-caps">{label}</Label>{children}</div>
);
const Row = ({ k, v }) => (
  <div className="flex justify-between gap-4"><span className="text-[#475569]">{k}</span><span className="font-semibold text-right truncate">{v}</span></div>
);

function CityCoverage({ area, onAddPin, onRemovePin, onRemoveCity }) {
  const [pin, setPin] = useState("");
  const add = async () => { await onAddPin(area.city, pin); setPin(""); };
  return (
    <div className="border border-[#E2E8F0] rounded-xl p-3.5" data-testid={`area-${area.city}`}>
      <div className="flex items-center justify-between">
        <p className="font-bold flex items-center gap-1.5"><MapPin size={16} weight="fill" className="text-[#4F46E5]" /> {area.city}</p>
        <button onClick={() => onRemoveCity(area.city)} className="text-[#94a3b8] hover:text-rose-500" data-testid={`remove-city-${area.city}`}><X size={16} /></button>
      </div>
      <div className="flex gap-2 mt-3">
        <Input value={pin} maxLength={6} onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))} placeholder="Pincode you cover" className="rounded-lg" data-testid={`pin-input-${area.city}`} />
        <Button type="button" variant="outline" className="rounded-lg gap-1 shrink-0" onClick={add} data-testid={`add-pin-${area.city}`}><Plus size={15} /> Add</Button>
      </div>
      <div className="flex flex-wrap gap-2 mt-3">
        {area.pincodes.map((p) => (
          <span key={p.pincode} className="inline-flex items-center gap-1.5 bg-[#eef2ff] text-[#4F46E5] text-xs font-semibold px-2.5 py-1 rounded-full" data-testid={`pin-chip-${p.pincode}`}>
            {p.pincode}{p.area ? ` · ${p.area}` : ""}
            <button onClick={() => onRemovePin(area.city, p.pincode)}><X size={12} weight="bold" /></button>
          </span>
        ))}
        {area.pincodes.length === 0 && <span className="text-xs text-[#94a3b8]">No pincodes added yet</span>}
      </div>
    </div>
  );
}
